import React, { useState, useEffect, useRef } from 'react';
import { text } from '../../data';
import { useProject } from '../../project-details/context/ProjectContext';
import SaveToProjectDropDown from '../SaveToProjectDropDown';
import { createProjectSeals } from '../../api/api';
import { isAuthenticated } from '../../../sso';
import toast from 'react-hot-toast';
import { saveNewProjectClickAnalyticsHandler, saveDropdownSelectionAnalyticsHandler, saveDropDownOpenAnalyticsHandler } from '../../analytics';

type SaveSealDropdownProps = {
  brand: string;
  housingBore: string;
  material: string;
  sealPartDescription: string;
  sealPartImageUrl: string;
  sealPartNumber: string;
  shaftDiameter: string;
  type: string;
  width: string;
};

const SaveSealDropdown: React.FC<SaveSealDropdownProps> = ({
	brand,
	housingBore,
	material,
	sealPartDescription,
	sealPartImageUrl,
	sealPartNumber,
	shaftDiameter,
	type,
	width,
}) => {
	const { projects, refreshProjects, loading } = useProject();
	const [ isLoggedIn, setIsLoggedIn ] = useState(false);
	const [ isOpen, setIsOpen ] = useState(false);
	const [ isLoading, setIsLoading ] = useState(false);
	const dropdownRef = useRef<HTMLDivElement | null>(null);
	const toggleMenu = (event) => {
		saveDropDownOpenAnalyticsHandler(event, dropdownRef, isOpen, [ sealPartNumber ], 'Add to Project', 'seal part');
		setIsOpen((prev) => ! prev);
	};

	const trackSaveDropdownSelection = (event) => {
		saveDropdownSelectionAnalyticsHandler(event, dropdownRef, 'Add to Project', 'seal part');
	};

	// listen SSO account loaded
	useEffect(() => {
		setIsLoading(true);
		const handleAccountInfoLoaded = async () => {
			const isLogged = await isAuthenticated();
			setIsLoggedIn(isLogged);
			setIsLoading(false);
		};

		document.addEventListener('timken_sso_account_info_loaded', handleAccountInfoLoaded);

		return () => {
			document.removeEventListener('timken_sso_account_info_loaded', handleAccountInfoLoaded);
		};
	}, []);

	useEffect(() => {
		if (isLoggedIn) {
			setIsLoading(loading);
		}
	}, [ loading, isLoggedIn ]);

	/**
	 * Close dropdown if user clicks outside of dropdown container
	 */
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (dropdownRef.current && ! dropdownRef.current.contains(event.target as Node)) {
				setIsOpen(false);
			}
		};

		if (isOpen) {
			document.addEventListener('click', handleClickOutside);
		}

		return () => document.removeEventListener('click', handleClickOutside);
	}, [ isOpen ]);

	/**
	 * Refresh project data when a new project is created
	 *
	 * @param  createdProjectId
	 */
	const onNewProjectCreated = async (createdProjectId) => {
		setIsOpen(false);
		saveNewProjectClickAnalyticsHandler(dropdownRef, 'Add to Project', 'seal part');
		await saveSealToProject(createdProjectId); // save seal to the created project
	};

	/**
	 * Save seal part to project
	 *
	 * @param  projectId
	 */
	const saveSealToProject = async (projectId: string) => {
		if (! projectId) {
			return;
		}

		try {
			setIsLoading(true);

			const payload = {
				projectId,
				displayedUnit: 'M', // M is used in the template to populate dimensions
				brand,
				housingBore,
				material,
				sealPartDescription,
				sealPartImageUrl,
				sealPartNumber,
				shaftDiameter,
				type,
				width,
			};

			const toastId = toast.loading('Loading...');
			await createProjectSeals(payload);
			toast.success(`${ sealPartNumber } added to the project`, {
				id: toastId,
			});

			setIsLoading(false);
			refreshProjects();
		} catch (error) {
			setIsLoading(false);
			toast.dismiss();
			toast.error(error.message);
		}
	};

	const handleSaveSealsClick = (event: React.MouseEvent<HTMLElement, MouseEvent>, projectId: string) => {
		event.preventDefault();
		saveDropdownSelectionAnalyticsHandler(event, dropdownRef, 'Add to Project', 'seal part');
		saveSealToProject(projectId);
		setIsOpen(false);
	}

	return (
		<div className={`dropdown ${ isLoading ? 't-full-block-skeleton' : '' }`} ref={dropdownRef} data-item-number={sealPartNumber} data-project-count={projects.length}>
			<button
				className="btn btn--secondary dropdown-toggle"
				type="button"
				id={'seals-save__toggle'}
				aria-expanded={isOpen}
				onClick={toggleMenu}
				disabled={! isLoggedIn}
			>
				<i className={isLoggedIn ? 'icon-folder-outline' : 'icon-lock'}></i> <span className="action__text">{text.addToProject}</span>
			</button>

			<SaveToProjectDropDown
				projects={projects}
				projectItemCompareValue={sealPartNumber}
				targetType='seals'
				isOpen={isOpen}
				onSuccess={onNewProjectCreated}
				onModalOpen={trackSaveDropdownSelection}
				onSaveClick={handleSaveSealsClick}
			>
			</SaveToProjectDropDown>
		</div>
	);
};

export default SaveSealDropdown;
